import { useCallback, useEffect, useState, type DragEvent, type MouseEvent as ReactMouseEvent } from 'react';
import {
    Background,
    BackgroundVariant,
    Controls,
    MiniMap,
    ReactFlow,
    ReactFlowProvider,
    useReactFlow,
    useOnViewportChange,
    useKeyPress,
    type Node,
    type OnNodeDrag,
} from '@xyflow/react';
import { nodeTypes, paletteItems } from '../model/nodeTypes/registry';
import { useProjectStore, orderNodesParentFirst, resolveAbsolutePosition, genId } from '../store/projectStore';
import { PALETTE_DND_TYPE } from '../panels/Palette';
import { useColorMode } from './useColorMode';
import { ContextMenu } from './ContextMenu';
import { ConfirmDialog } from './ConfirmDialog';

const DEFAULT_NODE_WIDTH = 150;
const DEFAULT_NODE_HEIGHT = 60;
const DEFAULT_GROUP_WIDTH = 200;
const DEFAULT_GROUP_HEIGHT = 150;

function Canvas() {
    const nodes = useProjectStore((state) => state.nodes);
    const edges = useProjectStore((state) => state.edges);
    const onNodesChange = useProjectStore((state) => state.onNodesChange);
    const onEdgesChange = useProjectStore((state) => state.onEdgesChange);
    const onConnect = useProjectStore((state) => state.onConnect);
    const addNodeAt = useProjectStore((state) => state.addNodeAt);
    const colorMode = useColorMode();
    const { screenToFlowPosition, deleteElements, getNodes, setNodes, getEdges, setEdges } = useReactFlow();

    // Context menus (right-click on the pane vs. on a node) and the
    // delete-confirmation dialog they can lead to.
    const [paneMenu, setPaneMenu] = useState<{ x: number; y: number; flowX: number; flowY: number } | null>(null);
    const [nodeMenu, setNodeMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);

    // Close any open menu as soon as the canvas is panned/zoomed — a menu
    // anchored to a screen position becomes meaningless (and visually
    // detached from whatever it was opened on) once the viewport moves.
    useOnViewportChange({
        onStart: () => {
            setPaneMenu(null);
            setNodeMenu(null);
        },
    });

    const onPaneContextMenu = useCallback(
        (event: MouseEvent | ReactMouseEvent) => {
            event.preventDefault();
            setNodeMenu(null);
            const flowPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            setPaneMenu({ x: event.clientX, y: event.clientY, flowX: flowPosition.x, flowY: flowPosition.y });
        },
        [screenToFlowPosition],
    );

    const onNodeContextMenu = useCallback((event: ReactMouseEvent, node: Node) => {
        event.preventDefault();
        setPaneMenu(null);
        setNodeMenu({ x: event.clientX, y: event.clientY, nodeId: node.id });
    }, []);

    const handleDuplicateNode = useCallback(
        (nodeId: string) => {
            const source = getNodes().find((n) => n.id === nodeId);
            if (!source) return;

            // Offset so the copy doesn't land exactly on top of the
            // original; keeps the same parentId (if any) so it stays
            // inside whatever Group frame the source was in, and the
            // offset is applied in the same parent-relative coordinate
            // space `position` is already expressed in.
            const duplicate: Node = {
                ...source,
                id: genId(),
                position: { x: source.position.x + 40, y: source.position.y + 40 },
                selected: false,
                data: { ...source.data },
            };

            setNodes((current) => [...current, duplicate]);
        },
        [getNodes, setNodes],
    );

    const handleRequestDelete = useCallback(
        (nodeId: string) => {
            const node = getNodes().find((n) => n.id === nodeId);
            if (!node) return;
            const label =
                (node.data?.display_name as string) ||
                (node.data?.label as string) ||
                (node.data?.text as string) ||
                node.type ||
                'this node';
            setDeleteTarget({ id: nodeId, label });
        },
        [getNodes],
    );

    const handleConfirmDelete = useCallback(() => {
        if (!deleteTarget) return;
        deleteElements({ nodes: [{ id: deleteTarget.id }] });
        setDeleteTarget(null);
    }, [deleteTarget, deleteElements]);

    // Keyboard handlers
    const deletePressed = useKeyPress(['Delete', 'Backspace']);
    const tabPressed = useKeyPress('Tab');
    const arrowUpPressed = useKeyPress('ArrowUp');
    const arrowDownPressed = useKeyPress('ArrowDown');
    const arrowLeftPressed = useKeyPress('ArrowLeft');
    const arrowRightPressed = useKeyPress('ArrowRight');

    // Delete selected nodes.
    // Previously `useCallback` — the callback was created but never called
    // or attached to anything, so this silently did nothing on every
    // render. `useEffect` actually runs the body when `deletePressed`
    // becomes true.
    useEffect(() => {
        if (!deletePressed) return;
        const selectedNodes = nodes.filter((n) => (n.selected as any) === true);
        if (selectedNodes.length > 0) {
            deleteElements({ nodes: selectedNodes.map((n) => ({ id: n.id })) });
        }
    }, [deletePressed, nodes, deleteElements]);

    // Arrow-key nudge. Same `useCallback` → `useEffect` fix as above.
    useEffect(() => {
        if (!arrowUpPressed && !arrowDownPressed && !arrowLeftPressed && !arrowRightPressed) return;

        const selectedNodes = getNodes().filter((n) => n.selected);
        if (selectedNodes.length === 0) return;

        const updated = selectedNodes.map((n) => ({
            ...n,
            position: {
                x: n.position.x + (arrowRightPressed ? 10 : arrowLeftPressed ? -10 : 0),
                y: n.position.y + (arrowDownPressed ? 10 : arrowUpPressed ? -10 : 0),
            },
        }));

        setNodes((oldNodes: any[]) =>
            oldNodes.map((n: any) => updated.find((u) => u.id === n.id) || n),
        );
    }, [arrowUpPressed, arrowDownPressed, arrowLeftPressed, arrowRightPressed, getNodes, setNodes]);

    // Tab to create next fragment. Same `useCallback` → `useEffect` fix as above.
    useEffect(() => {
        if (!tabPressed) return;

        const selectedNodes = nodes.filter((n) => (n.selected as any) === true);
        if (selectedNodes.length !== 1 || selectedNodes[0].type !== 'fragment') return;

        const sourceNode = selectedNodes[0];
        const nextX = sourceNode.position.x + 300;
        const nextY = sourceNode.position.y;

        // Create new fragment
        addNodeAt('fragment', { x: nextX, y: nextY });

        // Connect source to new node (simplified — in production, wait for node creation)
        // For now, this creates the node; connection logic would be Phase 2 v2
    }, [tabPressed, nodes, addNodeAt]);

    /**
     * Drag-to-group detection (doc 06 §1 Group frame: "dragging the frame
     * moves members" — the reverse direction, dragging a node *into* a
     * frame, is how membership is established in the first place; nothing
     * else in the app currently offers a way to do that). Dropping a node
     * so its center lands inside a Group frame's bounds parents it to that
     * frame; dragging it back out un-parents it. Position is converted
     * to/from parent-relative on the way in/out, since React Flow requires
     * a child's `position` to be relative to its parent (see
     * projectStore.ts's block comment above genId() for the full wire-format
     * rationale).
     */
    const onNodeDragStop: OnNodeDrag = useCallback(
        (_event, draggedNode) => {
            if (draggedNode.type === 'group') return; // groups aren't nested into other groups

            const liveNodes = getNodes();
            const byId = new Map(liveNodes.map((n) => [n.id, n]));

            const draggedAbs = resolveAbsolutePosition(draggedNode.id, byId);
            const draggedWidth = draggedNode.measured?.width ?? draggedNode.width ?? DEFAULT_NODE_WIDTH;
            const draggedHeight = draggedNode.measured?.height ?? draggedNode.height ?? DEFAULT_NODE_HEIGHT;
            const draggedCenter = {
                x: draggedAbs.x + draggedWidth / 2,
                y: draggedAbs.y + draggedHeight / 2,
            };

            const candidateGroup = liveNodes.find((n) => {
                if (n.type !== 'group' || n.id === draggedNode.id) return false;
                const groupAbs = resolveAbsolutePosition(n.id, byId);
                const groupWidth = n.measured?.width ?? n.width ?? DEFAULT_GROUP_WIDTH;
                const groupHeight = n.measured?.height ?? n.height ?? DEFAULT_GROUP_HEIGHT;
                return (
                    draggedCenter.x >= groupAbs.x &&
                    draggedCenter.x <= groupAbs.x + groupWidth &&
                    draggedCenter.y >= groupAbs.y &&
                    draggedCenter.y <= groupAbs.y + groupHeight
                );
            });

            const newParentId = candidateGroup?.id;
            if (newParentId === draggedNode.parentId) return; // no change in membership

            setNodes((current: Node[]) => {
                const currentById = new Map(current.map((n) => [n.id, n]));
                const currentAbs = resolveAbsolutePosition(draggedNode.id, currentById);

                const updated = current.map((n) => {
                    if (n.id !== draggedNode.id) return n;

                    if (newParentId) {
                        const parentAbs = resolveAbsolutePosition(newParentId, currentById);
                        return {
                            ...n,
                            parentId: newParentId,
                            position: { x: currentAbs.x - parentAbs.x, y: currentAbs.y - parentAbs.y },
                        };
                    }

                    const { parentId: _drop, ...rest } = n;
                    return { ...rest, position: currentAbs };
                });

                return orderNodesParentFirst(updated);
            });
        },
        [getNodes, setNodes],
    );

    // Both dragenter and dragover must call preventDefault() for some
    // browsers to treat this element as a valid drop target at all — with
    // only dragover handled, those browsers silently refuse the drop.
    const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }, []);

    const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    }, []);

    const onDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            const type = event.dataTransfer.getData(PALETTE_DND_TYPE);
            if (!type) return;

            const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
            addNodeAt(type, position);
        },
        [addNodeAt, screenToFlowPosition],
    );

    const onConnectEnd = useCallback(
        (event: MouseEvent | TouchEvent) => {
            const clientX = 'clientX' in event ? event.clientX : event.touches?.[0]?.clientX || 0;
            const clientY = 'clientY' in event ? event.clientY : event.touches?.[0]?.clientY || 0;

            if ((event.target as HTMLElement)?.classList?.contains('react-flow__pane')) {
                const { x, y } = screenToFlowPosition({
                    x: clientX,
                    y: clientY,
                });
                // Show quick-create palette (Phase 2 v2)
                // For now, just create a hub at the drop point
                addNodeAt('hub', { x, y });
            }
        },
        [screenToFlowPosition, addNodeAt],
    );

    return (
        <div className="graph-canvas-wrapper" onDragEnter={onDragEnter} onDragOver={onDragOver} onDrop={onDrop}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onConnectEnd={onConnectEnd}
                onNodeDragStop={onNodeDragStop}
                onPaneContextMenu={onPaneContextMenu}
                onNodeContextMenu={onNodeContextMenu}
                onPaneClick={() => {
                    setPaneMenu(null);
                    setNodeMenu(null);
                }}
                colorMode={colorMode}
                proOptions={{ hideAttribution: true }}
                fitView
                fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
                minZoom={0.1}
                maxZoom={2.5}
                deleteKeyCode={['Backspace', 'Delete']}
                multiSelectionKeyCode="Shift"
                selectionOnDrag
            >
                <Background variant={BackgroundVariant.Dots} gap={20} />
                <MiniMap pannable zoomable nodeColor={() => 'var(--tblr-primary)'} />
                <Controls />
            </ReactFlow>

            {paneMenu && (
                <ContextMenu
                    x={paneMenu.x}
                    y={paneMenu.y}
                    onClose={() => setPaneMenu(null)}
                    items={paletteItems.map((item) => ({
                        label: item.label,
                        icon: item.icon,
                        onSelect: () => addNodeAt(item.type, { x: paneMenu.flowX, y: paneMenu.flowY }),
                    }))}
                />
            )}

            {nodeMenu && (
                <ContextMenu
                    x={nodeMenu.x}
                    y={nodeMenu.y}
                    onClose={() => setNodeMenu(null)}
                    items={[
                        {
                            label: 'Duplicate',
                            icon: 'ti-copy',
                            onSelect: () => handleDuplicateNode(nodeMenu.nodeId),
                        },
                        {
                            label: 'Delete',
                            icon: 'ti-trash',
                            danger: true,
                            onSelect: () => handleRequestDelete(nodeMenu.nodeId),
                        },
                    ]}
                />
            )}

            {deleteTarget && (
                <ConfirmDialog
                    title="Delete node?"
                    message={`This will permanently delete "${deleteTarget.label}". This can't be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}

export function GraphCanvas() {
    return (
        <ReactFlowProvider>
            <Canvas />
        </ReactFlowProvider>
    );
}
